import { useState, useEffect, useRef } from 'react';
import { useForm } from '@mantine/form';
import { Select, Button, Drawer, Group, Text, Box, Pill, ActionIcon, Tooltip, Paper } from '@mantine/core';
import { DatePickerInput, TimeInput, Calendar, DatePicker } from '@mantine/dates';
import {
  IconPencil,
  IconTrash,
  IconX,
  IconCheck,
  IconClock,
  IconInfoCircle,
  IconRefresh,
  IconClockEdit,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useMediaQuery } from '@mantine/hooks';
import timezoneData from '../entrypoints/utilities/timezones.json';

interface RecurringEventFieldsProps {
  form: any;
}

// Define interface for modified occurrences to store both date and time info
interface ModifiedOccurrence {
  originalDateId: number; // Store the original date's timestamp ID
  date: Date;
  startTime: string;
  endTime: string;
  isSkipped?: boolean; // Flag to mark skipped occurrences
  spansNextDay?: boolean; // Flag to indicate if the event spans to the next day
}

const TIMEZONE_OPTIONS = Object.entries(timezoneData).map(([value, label]) => ({
  value,
  label: `${label} (${value})`,
}));

export default function RecurringEventFields({ form }: RecurringEventFieldsProps) {
  const [recurrenceDates, setRecurrenceDates] = useState<Date[]>([]);
  const [modifiedOccurrences, setModifiedOccurrences] = useState<ModifiedOccurrence[]>([]);
  const [selectedOccurrence, setSelectedOccurrence] = useState<Date | null>(null);
  const [originalDateId, setOriginalDateId] = useState<number | null>(null);
  const [selectedTime, setSelectedTime] = useState({ start: '', end: '' });
  const [eventSpansNextDay, setEventSpansNextDay] = useState(false);
  const [occurrenceSpansNextDay, setOccurrenceSpansNextDay] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [ordinalOccurrence, setOrdinalOccurrence] = useState('');
  const [originalValues, setOriginalValues] = useState({
    start: '',
    end: '',
    spansNextDay: false,
  });

  const startTimeRef = useRef<HTMLInputElement>(null);
  const endTimeRef = useRef<HTMLInputElement>(null);
  const drawerStartTimeRef = useRef<HTMLInputElement>(null);
  const drawerEndTimeRef = useRef<HTMLInputElement>(null);

  const startPickerControl = (
    <ActionIcon variant="subtle" color="gray" onClick={() => startTimeRef.current?.showPicker()}>
      <IconClock size={16} stroke={1.5} />
    </ActionIcon>
  );
  const endPickerControl = (
    <ActionIcon variant="subtle" color="gray" onClick={() => endTimeRef.current?.showPicker()}>
      <IconClock size={16} stroke={1.5} />
    </ActionIcon>
  );

  const drawerStartPickerControl = (
    <ActionIcon variant="subtle" color="gray" onClick={() => drawerStartTimeRef.current?.showPicker()}>
      <IconClock size={16} stroke={1.5} />
    </ActionIcon>
  );
  const drawerEndPickerControl = (
    <ActionIcon variant="subtle" color="gray" onClick={() => drawerEndTimeRef.current?.showPicker()}>
      <IconClock size={16} stroke={1.5} />
    </ActionIcon>
  );

  // Calculate day of week for monthly recurrence
  const dayOfWeek = form.values.recurring_event_start_date
    ? dayjs(form.values.recurring_event_start_date).format('dddd')
    : '';

  // Generate a clean array of final occurrences for database submission
  const getFinalOccurrencesForSubmission = () => {
    return recurrenceDates
      .filter((date) => {
        // Filter out skipped occurrences
        const dateId = date.getTime();
        const modified = modifiedOccurrences.find(
          (occ) => occ.originalDateId === dateId || occ.date.getTime() === dateId,
        );

        return !(modified && modified.isSkipped);
      })
      .map((date) => {
        const dateId = date.getTime();
        const modified = modifiedOccurrences.find(
          (occ) => (occ.originalDateId === dateId || occ.date.getTime() === dateId) && !occ.isSkipped,
        );

        // Format the date and time as needed for your Rails backend
        return {
          date: date.toISOString().split('T')[0], // YYYY-MM-DD format
          start_time: modified ? modified.startTime : form.values.recurring_event_start_time,
          end_time: modified ? modified.endTime : form.values.recurring_event_end_time,
          spans_next_day: modified
            ? modified.spansNextDay !== undefined
              ? modified.spansNextDay
              : form.values.spans_next_day || false
            : form.values.spans_next_day || false,
        };
      });
  };

  // Export the occurrences data to the parent form
  useEffect(() => {
    // This will update a field in your form with the final occurrences data
    // whenever any relevant data changes
    const finalOccurrences = getFinalOccurrencesForSubmission();
    form.setFieldValue('recurring_event_occurrences', finalOccurrences);

    console.log('Final Occurrences for Submission:', finalOccurrences);
  }, [
    recurrenceDates,
    modifiedOccurrences,
    form.values.recurring_event_start_time,
    form.values.recurring_event_end_time,
  ]);

  useEffect(() => {
    console.log('Current value of eventSpansNextDay:', eventSpansNextDay);
  }, [eventSpansNextDay]);

  useEffect(() => {
    console.log('Current value of occurrenceSpansNextDay:', occurrenceSpansNextDay);
  }, [occurrenceSpansNextDay]);

  // Log the current state of recurrence dates and modifications
  useEffect(() => {
    // Get a clean representation of the data for logging
    const occurrencesData = recurrenceDates.map((date) => {
      const dateId = date.getTime();
      const modified = modifiedOccurrences.find(
        (occ) => occ.originalDateId === dateId || occ.date.getTime() === dateId,
      );

      return {
        date: dayjs(date).format('YYYY-MM-DD'),
        time:
          modified && modified.isSkipped
            ? 'SKIPPED'
            : modified
              ? `${modified.startTime} - ${modified.endTime}`
              : `${form.values.recurring_event_start_time} - ${form.values.recurring_event_end_time}`,
        status: modified ? (modified.isSkipped ? 'skipped' : 'modified') : 'default',
        originalDateId: modified?.originalDateId ? dayjs(new Date(modified.originalDateId)).format('YYYY-MM-DD') : null,
        spansNextDay: modified
          ? modified.spansNextDay !== undefined
            ? modified.spansNextDay
            : form.values.spans_next_day || false
          : form.values.spans_next_day || false,
      };
    });

    console.log('Current Occurrences:', occurrencesData);
    console.log('Raw Modified Occurrences:', modifiedOccurrences);
  }, [
    recurrenceDates,
    modifiedOccurrences,
    form.values.recurring_event_start_time,
    form.values.recurring_event_end_time,
    form.values.spans_next_day,
  ]);

  // Get maximum allowed occurrences for the current pattern
  const getMaxOccurrencesForPattern = (pattern: string) => {
    switch (pattern) {
      case 'Weekly':
        return 12; // Maximum of 12 occurrences for weekly
      case 'Bi-Weekly':
        return 6; // Maximum of 6 occurrences for bi-weekly
      case 'Monthly':
        return 3; // Maximum of 3 occurrences for monthly
      default:
        return 12; // Default to 12 if no pattern specified
    }
  };

  // Handle recurrence pattern change - enforce occurrence limits
  useEffect(() => {
    if (!form.values.recurrence_pattern) return;

    const maxAllowed = getMaxOccurrencesForPattern(form.values.recurrence_pattern);
    const currentValue = parseInt(form.values.num_occurrences || '0', 10);

    // If current value is greater than max allowed or not set, update it
    if (currentValue > maxAllowed || currentValue === 0) {
      form.setFieldValue('num_occurrences', maxAllowed);
      console.log(`Updated occurrences to ${maxAllowed} (max for ${form.values.recurrence_pattern})`);
    }

    // Clean up any modifications for dates that will no longer exist
    if (recurrenceDates.length > 0) {
      // We'll need to regenerate potential dates to compare against
      let baseDate = form.values.recurring_event_start_date
        ? new Date(form.values.recurring_event_start_date)
        : new Date();

      let potentialDates: Date[] = [];
      for (let i = 0; i < maxAllowed; i++) {
        let newDate = new Date(baseDate);

        if (form.values.recurrence_pattern === 'Weekly') newDate.setDate(baseDate.getDate() + i * 7);
        else if (form.values.recurrence_pattern === 'Bi-Weekly') newDate.setDate(baseDate.getDate() + i * 14);
        else if (form.values.recurrence_pattern === 'Monthly') newDate = dayjs(baseDate).add(i, 'month').toDate();

        potentialDates.push(newDate);
      }

      // Filter out modifications for dates that won't exist anymore
      setModifiedOccurrences((prevModifications) => {
        return prevModifications.filter((mod) => {
          // Check if any potential date matches this modification
          return potentialDates.some((date) => {
            const dateId = date.getTime();
            return mod.originalDateId === dateId || mod.date.getTime() === dateId;
          });
        });
      });
    }
  }, [form.values.recurrence_pattern]);

  // Generate Recurrence Dates
  useEffect(() => {
    const { recurring_event_start_date, recurrence_pattern, num_occurrences } = form.values;
    if (!recurring_event_start_date || !num_occurrences) return;

    let dates: Date[] = [];
    let baseDate = new Date(recurring_event_start_date);

    for (let i = 0; i < num_occurrences; i++) {
      let newDate = new Date(baseDate);

      if (recurrence_pattern === 'Weekly') newDate.setDate(baseDate.getDate() + i * 7);
      if (recurrence_pattern === 'Bi-Weekly') newDate.setDate(baseDate.getDate() + i * 14);
      if (recurrence_pattern === 'Monthly') {
        newDate = dayjs(baseDate).add(i, 'month').toDate();
      }

      dates.push(newDate);
    }

    // Set ordinal occurrence for monthly pattern
    if (recurrence_pattern === 'Monthly' && baseDate) {
      const day = baseDate.getDate();
      const weekNum = Math.ceil(day / 7);
      const ordinals = ['First', 'Second', 'Third', 'Fourth', 'Last'];
      setOrdinalOccurrence(ordinals[Math.min(weekNum - 1, 4)]);
    }

    setRecurrenceDates(dates);
  }, [form.values.recurring_event_start_date, form.values.recurrence_pattern, form.values.num_occurrences]);

  // Modify Occurrence
  const handleModify = (date: Date) => {
    const dateId = date.getTime();
    setOriginalDateId(dateId);
    setSelectedOccurrence(date);

    // Find an existing modification for this occurrence
    const modifiedOccurrence = modifiedOccurrences.find((occ) => occ.originalDateId === dateId);

    if (modifiedOccurrence) {
      setSelectedTime({
        start: modifiedOccurrence.startTime,
        end: modifiedOccurrence.endTime,
      });
      setOccurrenceSpansNextDay(modifiedOccurrence.spansNextDay ?? form.values.spans_next_day);
    } else {
      // Store the current values to check for modifications later
      setSelectedTime({
        start: form.values.recurring_event_start_time || '',
        end: form.values.recurring_event_end_time || '',
      });
      setOccurrenceSpansNextDay(form.values.spans_next_day || false);
    }

    // Store the original values for comparison when saving
    setOriginalValues({
      start: form.values.recurring_event_start_time || '',
      end: form.values.recurring_event_end_time || '',
      spansNextDay: form.values.spans_next_day || false,
    });

    setDrawerOpen(true);
  };

  const saveModification = () => {
    if (!selectedOccurrence || originalDateId === null) {
      console.error('❌ No occurrence selected for modification.');
      return;
    }

    console.log('📝 Time values before saving:', {
      start: selectedTime.start,
      end: selectedTime.end,
      spansNextDay: occurrenceSpansNextDay, // Updated this line
    });

    // Validate time format (HH:MM format)
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (!timeRegex.test(selectedTime.start) || !timeRegex.test(selectedTime.end)) {
      console.error('❌ Invalid time format. Expected HH:MM format.');
      return;
    }

    // Check if any modifications were made
    const dateWasChanged = selectedOccurrence.getTime() !== originalDateId;
    const timeWasChanged =
      selectedTime.start !== form.values.recurring_event_start_time ||
      selectedTime.end !== form.values.recurring_event_end_time;
    const spansNextDayChanged = occurrenceSpansNextDay !== form.values.spans_next_day;

    const hasModifications = dateWasChanged || timeWasChanged || spansNextDayChanged;

    if (!hasModifications) {
      console.log('✅ No modifications detected. Closing drawer.');
      setDrawerOpen(false);
      return;
    }

    // Find existing modification by originalDateId
    const existingIndex = modifiedOccurrences.findIndex((occ) => occ.originalDateId === originalDateId);

    // Update or add to modifiedOccurrences list
    const updatedOccurrence = {
      originalDateId,
      date: selectedOccurrence,
      startTime: selectedTime.start,
      endTime: selectedTime.end,
      spansNextDay: occurrenceSpansNextDay, // Updated this line
    };

    if (existingIndex >= 0) {
      const updatedOccurrences = [...modifiedOccurrences];
      updatedOccurrences[existingIndex] = updatedOccurrence;
      setModifiedOccurrences(updatedOccurrences);
    } else {
      setModifiedOccurrences([...modifiedOccurrences, updatedOccurrence]);
    }

    // Update the recurrence dates list if the date was changed
    if (dateWasChanged) {
      setRecurrenceDates((prevDates) =>
        prevDates.map((date) => (date.getTime() === originalDateId ? selectedOccurrence : date)),
      );
    }

    console.log('✅ Modification saved successfully.');
    setDrawerOpen(false);
  };

  // Get occurrence display time (either modified or default)
  const getOccurrenceTime = (date: Date) => {
    const dateId = date.getTime();
    const modified = modifiedOccurrences.find((occ) => {
      // Check if this is a modified occurrence by comparing its ID or the new date
      return (occ.originalDateId === dateId || occ.date.getTime() === dateId) && !occ.isSkipped;
    });

    if (modified) {
      return {
        start: modified.startTime,
        end: modified.endTime,
        spansNextDay: modified.spansNextDay !== undefined ? modified.spansNextDay : form.values.spans_next_day || false,
      };
    }

    return {
      start: form.values.recurring_event_start_time,
      end: form.values.recurring_event_end_time,
      spansNextDay: form.values.spans_next_day || false,
    };
  };

  // Check if an occurrence has been modified
  const isOccurrenceModified = (date: Date) => {
    const dateId = date.getTime();
    return modifiedOccurrences.some(
      (occ) => (occ.originalDateId === dateId || occ.date.getTime() === dateId) && !occ.isSkipped,
    );
  };

  // Check if an occurrence has been skipped
  const isOccurrenceSkipped = (date: Date) => {
    const dateId = date.getTime();
    return modifiedOccurrences.some(
      (occ) => (occ.originalDateId === dateId || occ.date.getTime() === dateId) && occ.isSkipped,
    );
  };

  // Get a specific occurrence by date
  const getOccurrence = (date: Date) => {
    const dateId = date.getTime();
    return modifiedOccurrences.find((occ) => occ.originalDateId === dateId || occ.date.getTime() === dateId);
  };

  // Format time string to AM/PM format
  const formatTimeToAMPM = (timeStr: string) => {
    if (!timeStr) return '';

    try {
      return dayjs(`2025-01-01 ${timeStr}`, 'YYYY-MM-DD HH:mm').format('h:mm A');
    } catch (e) {
      console.error('Error formatting time:', e);
      return timeStr; // Return original if format fails
    }
  };

  // Handle skipping an occurrence
  const handleSkipOccurrence = (date: Date) => {
    const dateId = date.getTime();

    // Check if this occurrence is already in the modified list
    const existingIndex = modifiedOccurrences.findIndex(
      (occ) => occ.originalDateId === dateId || occ.date.getTime() === dateId,
    );

    if (existingIndex >= 0) {
      // Update existing occurrence to be skipped
      const updatedOccurrences = [...modifiedOccurrences];
      updatedOccurrences[existingIndex] = {
        ...updatedOccurrences[existingIndex],
        isSkipped: true,
      };
      setModifiedOccurrences(updatedOccurrences);
    } else {
      // Add new skipped occurrence
      setModifiedOccurrences([
        ...modifiedOccurrences,
        {
          originalDateId: dateId,
          date,
          startTime: form.values.recurring_event_start_time || '',
          endTime: form.values.recurring_event_end_time || '',
          isSkipped: true,
        },
      ]);
    }
  };

  // Handle restoring an occurrence to original state
  const handleRestoreOccurrence = (date: Date) => {
    const dateId = date.getTime();

    // Remove this occurrence from the modified list to restore default behavior
    setModifiedOccurrences(
      modifiedOccurrences.filter((occ) => !(occ.originalDateId === dateId || occ.date.getTime() === dateId)),
    );

    // If this was a date change, restore the original date in the recurrence dates
    const modifiedOcc = modifiedOccurrences.find(
      (occ) => occ.originalDateId === dateId || occ.date.getTime() === dateId,
    );

    if (modifiedOcc && modifiedOcc.originalDateId !== modifiedOcc.date.getTime()) {
      // Find the original date in recurrence dates
      setRecurrenceDates((prevDates) => {
        return prevDates.map((d) => {
          if (d.getTime() === modifiedOcc.date.getTime()) {
            // Create a new date with the original ID
            const originalDate = new Date(modifiedOcc.originalDateId);
            return originalDate;
          }
          return d;
        });
      });
    }
  };

  return (
    <div>
      <Group grow mt="md">
        <DatePickerInput
          label="Recurring Event Start Date"
          placeholder="Select date"
          name="recurring_event_start_date"
          value={form.values.recurring_event_start_date ? new Date(form.values.recurring_event_start_date) : null}
          onChange={(date) => form.setFieldValue('recurring_event_start_date', date ? date.toISOString() : '')}
          required
          styles={{ label: { color: '#FFF7ED' } }}
        />

        <Select
          label="Recurrence Pattern"
          name="recurrence_pattern"
          styles={{ label: { color: '#FFF7ED' } }}
          data={['Weekly', 'Bi-Weekly', 'Monthly']}
          value={form.values.recurrence_pattern || ''}
          onChange={(value) => form.setFieldValue('recurrence_pattern', value)}
        />
      </Group>

      <Group grow mt="md">
        <div style={{ flex: '1 1 250px', minWidth: '135px' }}>
          <TimeInput
            label="Start Time"
            name="recurring_event_start_time"
            value={form.values.recurring_event_start_time}
            onChange={(e) => form.setFieldValue('recurring_event_start_time', e.target.value)}
            rightSection={startPickerControl}
            ref={startTimeRef}
            required
            styles={{ label: { color: '#FFF7ED' } }}
          />
        </div>
        <div style={{ flex: '1 1 250px', minWidth: '150px' }}>
          <TimeInput
            label="End Time"
            name="recurring_event_end_time"
            value={form.values.recurring_event_end_time}
            ref={endTimeRef}
            rightSection={endPickerControl}
            onChange={(e) => form.setFieldValue('recurring_event_end_time', e.target.value)}
            required
            styles={{ label: { color: '#FFF7ED' } }}
          />
        </div>

        {/* Spans Next Day Section */}
      </Group>

      {/* Recurrence Pattern */}
      <Group grow mt="md">
        {/* Recurrence Pattern & # of Occurrences */}

        <div
          style={{
            flex: '1 1 0%',
            display: 'flex',
            flexDirection: 'column',
            minWidth: isMobile ? '200px' : 'auto',
          }}
        >
          <div
            className="mt-1"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '-8px',
              marginTop: '-4px',
            }}
          >
            <Text size="sm" className="mb-1" fw={500} style={{ color: '#FFF7ED' }}>
              Next Day?
            </Text>
            <Tooltip
              label="Select 'Ends Next Day' if your occurrence continues past midnight or ends same day if it ends before midnight."
              className="mb-1"
              multiline
              w={300}
              withArrow
              color="orange"
            >
              <ActionIcon variant="transparent" color="orange" size="sm">
                <IconInfoCircle size={16} />
              </ActionIcon>
            </Tooltip>
          </div>
          <Select
            className="mt-2"
            data={['Ends Same Day', 'Ends Next Day']}
            value={eventSpansNextDay ? 'Ends Next Day' : 'Ends Same Day'}
            defaultValue="Ends Same Day"
            onChange={(newValue) => {
              console.log('Event duration selected:', newValue);
              setEventSpansNextDay(newValue === 'Ends Next Day');
            }}
            styles={{ label: { color: '#FFF7ED' } }}
            clearable
          />
        </div>

        <Select
          label="# of Occurrences"
          name="num_occurrences"
          styles={{ label: { color: '#FFF7ED' } }}
          data={
            form.values.recurrence_pattern === 'Weekly'
              ? Array.from({ length: 11 }, (_, i) => (i + 2).toString())
              : form.values.recurrence_pattern === 'Bi-Weekly'
                ? Array.from({ length: 5 }, (_, i) => (i + 2).toString())
                : Array.from({ length: 2 }, (_, i) => (i + 2).toString())
          }
          value={form.values.num_occurrences?.toString() || '2'}
          onChange={(value) => form.setFieldValue('num_occurrences', value ? parseInt(value, 10) : 0)}
        />
      </Group>

      {form.values.recurrence_pattern === 'Monthly' && (
        <Group grow mt="md">
          <Box style={{ backgroundColor: '#222', padding: '4px', borderRadius: '8px', textAlign: 'center' }}>
            <Text size="sm" c="orange">
              Ordinal Occurrence
            </Text>
            <Pill size="md" c="orange" mb="md">
              {ordinalOccurrence}
            </Pill>
          </Box>
          <Box style={{ backgroundColor: '#222', padding: '4px', borderRadius: '8px', textAlign: 'center' }}>
            <Text size="sm" c="orange">
              Day of the Week
            </Text>
            <Pill size="md" c="orange" mb="md">
              {dayOfWeek}
            </Pill>
          </Box>
        </Group>
      )}

      <div>
        {recurrenceDates.map((date, index) => {
          const modified = isOccurrenceModified(date);
          const skipped = isOccurrenceSkipped(date);
          const occurrenceTime = getOccurrenceTime(date);

          const formattedStartTime = formatTimeToAMPM(occurrenceTime.start);
          const formattedEndTime = formatTimeToAMPM(occurrenceTime.end);

          return (
            <Paper
              key={`${date.toISOString()}-${index}`}
              shadow="xs"
              withBorder
              p="xs"
              mt="sm"
              style={{
                backgroundColor: 'transparent',
                opacity: skipped ? 0.5 : 1,
              }}
            >
              <Group justify="space-between">
                <Group>
                  <Text
                    size="sm"
                    fw={500}
                    style={{
                      color: '#FFF7ED',
                      textDecoration: skipped ? 'line-through' : 'none',
                    }}
                  >
                    {dayjs(date).format('ddd, MMM D, YYYY')} {' - '}
                    {skipped ? 'SKIPPED' : `${formattedStartTime} to ${formattedEndTime}`}
                  </Text>
                  {modified && (
                    <Tooltip label="Modified Occurrence" withArrow>
                      <IconClockEdit size={18} color="orange" />
                    </Tooltip>
                  )}
                  {skipped && (
                    <Tooltip label="Skipped Occurrence" withArrow>
                      <IconX size={18} color="red" />
                    </Tooltip>
                  )}
                </Group>
                <Group>
                  {!skipped && (
                    <Button
                      variant="outline"
                      color="white"
                      size="md"
                      onClick={() => handleModify(date)}
                      styles={{ root: { padding: '0.75rem' } }}
                    >
                      <IconPencil size={20} />
                    </Button>
                  )}

                  {(modified || skipped) && (
                    <Button
                      color="orange"
                      size="md"
                      onClick={() => handleRestoreOccurrence(date)}
                      styles={{ root: { padding: '0.75rem' } }}
                    >
                      <IconRefresh size={20} />
                    </Button>
                  )}

                  {!skipped && (
                    <Button
                      color="red"
                      size="md"
                      onClick={() => handleSkipOccurrence(date)}
                      styles={{ root: { padding: '0.75rem' } }}
                    >
                      <IconTrash size={20} />
                    </Button>
                  )}
                </Group>
              </Group>
            </Paper>
          );
        })}
      </div>

      {/* Modify Drawer */}
      <Drawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title="Modify Occurrence"
        position="right"
        styles={{
          content: { backgroundColor: '#3d3c3e' },
          header: { backgroundColor: '#2c2b2d', marginBottom: '2rem' },
          title: { color: '#f2f2f2', fontWeight: 600, fontSize: '1.5rem' },
        }}
      >
        {/* Select Occurrence Date */}
        <DatePickerInput
          label="Occurrence Date"
          value={selectedOccurrence}
          onChange={(date) => {
            if (date) {
              setSelectedOccurrence(date);
              console.log('Date changed to:', date);
            }
          }}
          styles={{ label: { color: '#FFF7ED' } }}
        />

        {/* Modify Start Time */}
        <TimeInput
          label="Start Time"
          value={selectedTime.start}
          styles={{ label: { color: '#FFF7ED' } }}
          rightSection={drawerStartPickerControl}
          ref={drawerStartTimeRef}
          mt="md"
          onChange={(e) => {
            console.log('Start time changed to:', e.target.value);
            setSelectedTime({ ...selectedTime, start: e.target.value });
          }}
        />

        {/* Modify End Time */}
        <TimeInput
          label="End Time"
          value={selectedTime.end}
          styles={{ label: { color: '#FFF7ED' } }}
          rightSection={drawerEndPickerControl}
          ref={drawerEndTimeRef}
          mt="md"
          onChange={(e) => {
            console.log('End time changed to:', e.target.value);
            setSelectedTime({ ...selectedTime, end: e.target.value });
          }}
        />

        {/* Spans Next Day Option */}
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginTop: '16px',
              marginBottom: '8px',
            }}
          >
            <Text size="sm" fw={500} style={{ color: '#FFF7ED' }}>
              Next Day?
            </Text>
            <Tooltip
              label="Select 'Ends Next Day' if your occurrence continues past midnight or 'Ends Same Day' if it ends before midnight."
              multiline
              w={300}
              withArrow
              color="orange"
            >
              <ActionIcon variant="transparent" color="orange" size="sm">
                <IconInfoCircle size={16} />
              </ActionIcon>
            </Tooltip>
          </div>
          <Select
            label="Event Duration"
            data={['Ends Same Day', 'Ends Next Day']}
            value={occurrenceSpansNextDay ? 'Ends Next Day' : 'Ends Same Day'}
            defaultValue="Ends Same Day"
            className="mt-5"
            onChange={(newValue) => {
              console.log('Occurrence duration selected:', newValue);
              setOccurrenceSpansNextDay(newValue === 'Ends Next Day');
            }}
            styles={{ label: { color: '#FFF7ED' } }}
            clearable
          />
        </div>

        {/* Save Button */}
        <Group justify="right" mt="xl">
          <Button color="gray" onClick={() => setDrawerOpen(false)}>
            Cancel
          </Button>
          <Button onClick={saveModification} leftSection={<IconCheck size={16} />}>
            Save
          </Button>
        </Group>
      </Drawer>
    </div>
  );
}
